import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import FormLabel from '@mui/material/FormLabel';
import type { SxProps, Theme } from '@mui/material/styles';
import { type ReactNode, createContext, use, useId } from 'react';

import { visuallyHidden } from '../../lib/visually-hidden';
import { CONTROL_SIZE_ATTRIBUTE } from '../../theme/controls';
import { cssVar } from '../../theme/tokens';
import { type ControlSize, MUI_SIZE } from './sizes';

/** Label, hint and error props every field component accepts. */
export interface FieldProps {
  /** Shown above the control. Omit only when the control has its own accessible name. */
  label?: ReactNode;
  /** Keeps the label for screen readers but hides it (toolbars where a placeholder suffices). */
  hideLabel?: boolean;
  /** Help text under the control. */
  hint?: ReactNode;
  /** Error message (or `true` to mark the field invalid without text). */
  error?: ReactNode;
  required?: boolean;
  disabled?: boolean;
  size?: ControlSize;
  fullWidth?: boolean;
  /** Id of the control; generated when omitted. */
  id?: string;
  className?: string;
  sx?: SxProps<Theme>;
}

export interface FormFieldContextValue {
  /** Id for the control element (the label's `htmlFor`). */
  controlId: string;
  labelId: string;
  /** Ids of the hint and error, for the control's `aria-describedby`. */
  describedBy: string | undefined;
  invalid: boolean;
  required: boolean;
  disabled: boolean;
  size: ControlSize;
}

/** Splits a field component's props into the FormField's and the control's. */
export function splitFieldProps<P extends FieldProps>(props: P) {
  const {
    label,
    hideLabel,
    hint,
    error,
    required,
    disabled,
    size,
    fullWidth,
    id,
    className,
    sx,
    ...control
  } = props;
  return {
    field: {
      label,
      hideLabel,
      hint,
      error,
      required,
      disabled,
      size,
      fullWidth,
      id,
      className,
      sx,
    },
    control,
  };
}

const FormFieldContext = createContext<FormFieldContextValue | null>(null);

/** The surrounding FormField's ids and state; field components wire their control with it. */
export function useFormField(): FormFieldContextValue {
  const field = use(FormFieldContext);
  if (!field) throw new Error('useFormField must be used inside a FormField');
  return field;
}

/**
 * The one label pattern of the admin web: label above the control, hint and error below, a
 * required marker, and the ids that tie them to the control (`htmlFor`, `aria-describedby`;
 * MUI marks the control invalid and required from the surrounding form control).
 */
export function FormField({
  label,
  hideLabel = false,
  hint,
  error,
  required = false,
  disabled = false,
  size = 'md',
  fullWidth = false,
  id,
  className,
  sx,
  children,
}: FieldProps & { children: ReactNode }) {
  const generatedId = useId();
  const controlId = id ?? generatedId;
  const labelId = `${controlId}-label`;
  const hintId = hint ? `${controlId}-hint` : undefined;
  const hasErrorText = error !== undefined && error !== null && error !== false && error !== true;
  const errorId = hasErrorText ? `${controlId}-error` : undefined;
  const invalid = Boolean(error);
  const describedBy = [errorId, hintId].filter(Boolean).join(' ') || undefined;

  return (
    <FormControl
      fullWidth={fullWidth}
      error={invalid}
      required={required}
      disabled={disabled}
      size={MUI_SIZE[size]}
      // Sizes controls that only know small and medium (MUI X date fields).
      {...{ [CONTROL_SIZE_ATTRIBUTE]: size }}
      className={className}
      sx={sx}
    >
      {label ? (
        <FormLabel
          id={labelId}
          htmlFor={controlId}
          sx={
            hideLabel
              ? visuallyHidden
              : {
                  mb: 1.5,
                  typography: 'subtitle2',
                  color: cssVar('text'),
                  '&.Mui-focused': { color: cssVar('text') },
                  '&.Mui-error': { color: cssVar('danger-text') },
                  '& .MuiFormLabel-asterisk': { color: cssVar('danger-text') },
                }
          }
        >
          {label}
        </FormLabel>
      ) : null}
      <FormFieldContext
        value={{ controlId, labelId, describedBy, invalid, required, disabled, size }}
      >
        {children}
      </FormFieldContext>
      {hint ? (
        <FormHelperText id={hintId} error={false} sx={{ mx: 0, mt: 1.5 }}>
          {hint}
        </FormHelperText>
      ) : null}
      {hasErrorText ? (
        <FormHelperText id={errorId} sx={{ mx: 0, mt: 1.5 }}>
          {error}
        </FormHelperText>
      ) : null}
    </FormControl>
  );
}
